import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';

const STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

// Makes a navigation block a single tab stop: one link is tabbable and the
// arrow keys move between them.
@Directive({
    selector: '[appRovingTabindex]',
    standalone: true,
})
export class RovingTabindexDirective implements AfterViewInit, OnDestroy {

  private observer?: MutationObserver;

  constructor(private el: ElementRef<HTMLElement>) { }

  ngAfterViewInit() {
    this.activate(0);
    this.observer = new MutationObserver(() => this.activate(0));
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const links = this.links();
    const current = links.indexOf(document.activeElement as HTMLElement);
    if (current === -1) {
      return;
    }

    let next: number;
    if (STEP[event.key] !== undefined) {
      next = current + STEP[event.key];
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = links.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activate(next)?.focus();
  }

  @HostListener('focusin', ['$event'])
  onFocusin(event: FocusEvent) {
    this.activate(this.links().indexOf(event.target as HTMLElement));
  }

  private links(): HTMLElement[] {
    return Array.from(this.el.nativeElement.querySelectorAll<HTMLElement>('a'))
      .filter(link => link.offsetParent !== null);
  }

  private activate(index: number): HTMLElement | undefined {
    const links = this.links();
    const active = Math.min(Math.max(index, 0), links.length - 1);
    links.forEach((link, i) => link.tabIndex = i === active ? 0 : -1);
    return links[active];
  }
}
